/* TreatmentPlanFullView — the formal Invisique treatment-plan document.
 *
 * Route: /treatment-plan/:caseId  (top-level, NO portal sidebar chrome)
 *
 * Opens in a new browser tab from the doctor's Plan Approval Hero
 * ("View entire treatment plan" card). Designed to feel like a real
 * clinical document the planning service sends to the treating doctor —
 * letterhead, patient identity block, plan meta strip, the full plan body,
 * planner sign-off, and an Invisique footer.
 *
 * Print-friendly: top toolbar and certain interactive accents are hidden
 * via `print:hidden` so doctors can print or PDF-export this page
 * directly from the browser.
 */
import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
    CaretLeft, Printer, Quotes, CheckCircle, ArrowUUpLeft,
    CalendarBlank, User as UserIcon, Stethoscope, Buildings, Hash,
    Sparkle, ArrowUpRight, ArrowsClockwise, FileText,
    SealCheck,
} from "@phosphor-icons/react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import InvisiqueLogo from "@/components/site/Logo";
import PlanApprovalHero from "@/components/case/PlanApprovalHero";


/* ─────────── Formatters ─────────── */

const fmtINR = (n) => {
    if (n == null || n === "") return "—";
    const v = Number(n);
    if (!Number.isFinite(v)) return "—";
    return `₹${Math.round(v).toLocaleString("en-IN")}`;
};

const fmtLongDate = (iso) => {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleDateString("en-IN", {
            day: "numeric", month: "long", year: "numeric",
        });
    } catch { return "—"; }
};

const fmtDateTime = (iso) => {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleString("en-IN", {
            day: "numeric", month: "short", year: "numeric",
            hour: "numeric", minute: "2-digit",
        });
    } catch { return "—"; }
};

const computeAge = (dob) => {
    if (!dob) return null;
    try {
        const d = new Date(dob);
        const diff = Date.now() - d.getTime();
        const ageDt = new Date(diff);
        return Math.abs(ageDt.getUTCFullYear() - 1970);
    } catch { return null; }
};

const humanize = (s) => (s ? String(s).replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()) : "");

/* iter98.99cr — Display labels for refinement_expected_reason slugs
   (mirror of REFINEMENT_REASONS in PlanTimelineSection.jsx). */
const REFINEMENT_REASON_LABELS = {
    extraction: "Extraction case",
    class_iii: "Class III malocclusion",
    open_bite: "Open bite",
    deep_bite: "Deep bite",
    growth: "Skeletal growth / paediatric",
    other: "Other",
};
const humaniseRefinementReason = (slug) => {
    if (!slug) return "";
    return REFINEMENT_REASON_LABELS[slug] || humanize(slug);
};


/* ─────────── Status badge for the letterhead ─────────── */

const STATUS_META = {
    sent_for_approval:  { label: "Pending your approval",     tone: "primary" },
    ready_for_review:   { label: "Pending your approval",     tone: "primary" },
    approved:           { label: "Approved",                  tone: "emerald" },
    revision_requested: { label: "Revision requested",        tone: "rose"    },
    in_production:      { label: "In production",             tone: "primary" },
    assigned_to_production: { label: "Routed to production",  tone: "primary" },
    shipped:            { label: "Shipped to clinic",         tone: "primary" },
    in_treatment:       { label: "Active treatment",          tone: "primary" },
    treatment_started:  { label: "Active treatment",          tone: "primary" },
    completed:          { label: "Completed",                 tone: "emerald" },
};

const STATUS_PILL_CLS = {
    primary: "bg-primary/10 text-primary border-primary/30",
    emerald: "bg-emerald-100 text-emerald-800 border-emerald-200",
    rose:    "bg-rose-100 text-rose-800 border-rose-200",
    amber:   "bg-amber-100 text-amber-800 border-amber-200",
};


/* ─────────── Main page ─────────── */

export default function TreatmentPlanFullView() {
    const { caseId } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();

    const [c, setC] = useState(null);
    const [patient, setPatient] = useState(null);
    const [versions, setVersions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const load = async () => {
        setLoading(true);
        setError(null);
        try {
            const caseResp = await api.get(`/cases/${caseId}`);
            setC(caseResp.data);
            const tasks = [];
            if (caseResp.data?.patient_id) {
                tasks.push(
                    api.get(`/doctor/patients/${caseResp.data.patient_id}`)
                        .then((r) => setPatient(r.data))
                        .catch(() => { /* informational */ }),
                );
            }
            tasks.push(
                api.get(`/cases/${caseId}/plan-versions`)
                    .then((r) => setVersions(Array.isArray(r.data) ? r.data : []))
                    .catch(() => setVersions([])),
            );
            await Promise.all(tasks);
        } catch (err) {
            setError(err?.response?.data?.detail || "Failed to load treatment plan");
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); }, [caseId]);

    /* ── Derived data ── */
    const plan = c?.plan || {};
    const planVersion = plan?.version || 1;
    const sentForApprovalAt = useMemo(() => {
        const tl = c?.timeline || [];
        const evt = [...tl].reverse().find((e) =>
            e.action === "sent_for_approval" || e.action === "plan_sent_for_approval"
        );
        return evt?.created_at || plan?.updated_at || plan?.sent_at;
    }, [c]);
    const age = computeAge(patient?.dob);
    const statusMeta = STATUS_META[c?.status] || { label: humanize(c?.status), tone: "primary" };
    const setupFiles = (c?.files || []).filter((f) => f.kind === "setup" || f.kind === "setup_plan");

    /* Iterations list (chronological newest-first) — used in the
       "Plan revision history" section near the footer. */
    const iterations = useMemo(() => {
        if (!Array.isArray(versions)) return [];
        const list = [...versions]
            .filter((v) => v.reason === "sent_for_approval" || v.reason === "planner_resubmit" || v.reason === "revision_requested" || v.reason === "approved")
            .sort((a, b) => (b.version || 0) - (a.version || 0));
        return list;
    }, [versions]);

    /* ── Guard rails ── */
    if (loading) {
        return (
            <div className="min-h-screen grid place-items-center bg-secondary/30 text-muted-foreground">
                Loading treatment plan…
            </div>
        );
    }
    if (error || !c) {
        return (
            <div className="min-h-screen grid place-items-center bg-secondary/30 px-6">
                <div className="max-w-md bg-card border border-rose-200 rounded-2xl p-8 text-center text-rose-700">
                    <div className="font-semibold mb-1">Treatment plan unavailable</div>
                    <div className="text-sm">{error || "Case not found"}</div>
                    <Button
                        variant="outline"
                        className="mt-4"
                        onClick={() => navigate(-1)}
                        data-testid="treatment-plan-back-btn"
                    >
                        <CaretLeft size={12} weight="bold" className="mr-1" /> Back
                    </Button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-secondary/30 print:bg-white" data-testid="treatment-plan-document">
            {/* iter98.99bm — Print CSS: @page rules with explicit empty
                marginalia boxes suppress Chrome / Safari / Firefox default
                URL + date + page-title + page-number headers that otherwise
                appear at the top of every page when the doctor uses File →
                Print → Save as PDF. The styled letterhead inside the
                article is the ONLY header that should appear. */}
            <style>{`
                @media print {
                    @page {
                        size: A4;
                        margin: 12mm 12mm 14mm 12mm;
                        @top-left     { content: ""; }
                        @top-center   { content: ""; }
                        @top-right    { content: ""; }
                        @bottom-left  { content: ""; }
                        @bottom-center{ content: ""; }
                        @bottom-right { content: ""; }
                    }
                    html, body { background: white !important; }
                    /* Avoid splitting key blocks across pages where possible */
                    [data-testid="treatment-plan-letterhead"],
                    [data-testid="treatment-plan-identity"],
                    [data-testid="treatment-plan-meta-strip"] {
                        page-break-inside: avoid;
                        break-inside: avoid;
                    }
                }
            `}</style>
            {/* Top toolbar — hidden when printing */}
            <Toolbar caseId={c.id} onPrint={() => window.print()} />

            {/* The document itself — paper-style card */}
            <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pb-16 print:px-0 print:pb-0">
                <article className="bg-card rounded-2xl shadow-[0_8px_32px_-8px_rgba(15,23,42,0.12)] ring-1 ring-border/70 overflow-hidden print:shadow-none print:ring-0 print:rounded-none">

                    {/* ── Letterhead ── */}
                    <Letterhead
                        caseCode={c.code || c.case_code || c.id?.slice(0, 8)}
                        planVersion={planVersion}
                        sentAt={sentForApprovalAt}
                        statusLabel={statusMeta.label}
                        statusTone={statusMeta.tone}
                    />

                    {/* ── Patient + Doctor identity card ── */}
                    <IdentityBlock
                        patient={patient}
                        c={c}
                        age={age}
                    />

                    {/* ── Plan meta strip (planning service, planner, software, sent date) ── */}
                    <MetaStrip
                        c={c}
                        plan={plan}
                        sentAt={sentForApprovalAt}
                    />

                    {/* ── Document body ── */}
                    <div className="px-6 sm:px-10 py-8 space-y-7">

                        {/* Salutation + planner's headline message */}
                        <Salutation
                            doctorName={c.doctor_name || user?.name}
                            patientName={c.patient_name}
                            plannerName={c.planner_name}
                            plannerNotes={plan.planner_notes}
                        />

                        {/* iter98.99cr — Refinement-expected advisory.
                            Rendered as a document-styled clinical
                            disclosure section so the formal treatment
                            plan ALWAYS captures the planner's heads-up,
                            regardless of case status (approval / approved /
                            in production / completed). */}
                        {plan.refinement_expected && (
                            <RefinementAdvisoryBlock
                                reason={humaniseRefinementReason(plan.refinement_expected_reason)}
                            />
                        )}

                        {/* The clinical plan body — re-uses PlanApprovalHero in
                            documentMode: all accordions expanded, no pricing
                            panel, no side-by-side cards. */}
                        <div data-testid="treatment-plan-clinical-body">
                            <PlanApprovalHero
                                c={c}
                                onChange={load}
                                setupFiles={setupFiles}
                                documentMode
                            />
                        </div>

                        {/* External 3D viewer block (separate from the plan body,
                            kept prominent because it's THE interactive deliverable) */}
                        {plan.external_link && (
                            <ExternalViewerBlock
                                href={plan.external_link}
                                software={plan.planning_software}
                            />
                        )}

                        {/* Attached deliverables (setup PDFs, stage trees, etc.) */}
                        {setupFiles.length > 0 && (
                            <DeliverablesBlock files={setupFiles} />
                        )}

                        {/* Plan revision history (only when there's been more
                            than one iteration) */}
                        {iterations.length > 1 && (
                            <RevisionHistoryBlock iterations={iterations} timeline={c.timeline || []} />
                        )}

                        {/* Sign-off + Doctor's pending action callout */}
                        <SignOff
                            plannerName={c.planner_name}
                            statusKey={c.status}
                            caseId={c.id}
                        />
                    </div>

                    {/* ── Document footer ── */}
                    <DocFooter generatedAt={new Date().toISOString()} caseCode={c.code || c.case_code || c.id?.slice(0, 8)} planVersion={planVersion} />
                </article>
            </div>
        </div>
    );
}


/* ─────────── Toolbar ─────────── */

function Toolbar({ caseId, onPrint }) {
    return (
        <div className="sticky top-0 z-30 bg-card/95 backdrop-blur border-b border-border print:hidden">
            <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 h-12 flex items-center justify-between gap-3">
                <Link
                    to={`/doctor/cases/${caseId}`}
                    className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-muted-foreground hover:text-foreground"
                    data-testid="treatment-plan-back-to-case"
                >
                    <CaretLeft size={12} weight="bold" /> Back to case
                </Link>
                <div className="flex items-center gap-2">
                    <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={onPrint}
                        className="h-8 text-[12px]"
                        data-testid="treatment-plan-print"
                    >
                        <Printer size={13} weight="duotone" className="mr-1.5" /> Print / Save as PDF
                    </Button>
                </div>
            </div>
        </div>
    );
}


/* ─────────── Letterhead ─────────── */

function Letterhead({ caseCode, planVersion, sentAt, statusLabel, statusTone }) {
    const pill = STATUS_PILL_CLS[statusTone] || STATUS_PILL_CLS.primary;
    return (
        <header
            className="relative px-6 sm:px-10 pt-8 pb-6 bg-gradient-to-br from-primary/[0.06] via-card to-card border-b border-border/70"
            data-testid="treatment-plan-letterhead"
        >
            {/* Subtle stamp grid in the background */}
            <div className="absolute inset-0 pointer-events-none opacity-[0.04]"
                 style={{ backgroundImage: "radial-gradient(circle at 1px 1px, currentColor 1px, transparent 0)", backgroundSize: "24px 24px" }} />

            <div className="relative flex items-start justify-between gap-6 flex-wrap">
                <div className="flex items-start gap-4">
                    <InvisiqueLogo size="lg" showTagline />
                </div>
                <div className="text-right">
                    <div className="text-[10px] font-bold tracking-[0.22em] uppercase text-primary">
                        Treatment Plan
                    </div>
                    <div className="font-display text-2xl font-bold text-foreground tracking-tight leading-tight mt-1">
                        From the Center of Excellence
                    </div>
                    <div className="text-[12px] text-muted-foreground mt-1 tabular-nums">
                        Document · {caseCode} · v{planVersion} · {fmtLongDate(sentAt)}
                    </div>
                </div>
            </div>

            <div className="relative mt-5 flex items-center justify-between gap-3 flex-wrap">
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-card/80 ring-1 ring-border text-[11px] font-semibold text-foreground/80">
                    <SealCheck size={13} weight="fill" className="text-primary" />
                    Issued by Invisique™ Center of Excellence
                </div>
                <span
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-[11px] font-bold uppercase tracking-[0.14em] ${pill}`}
                    data-testid="treatment-plan-status-pill"
                >
                    <Sparkle size={11} weight="fill" />
                    {statusLabel}
                </span>
            </div>
        </header>
    );
}


/* ─────────── Patient + Doctor identity block ─────────── */

function IdentityBlock({ patient, c, age }) {
    const patientFullName = patient?.name || c?.patient_name || "—";
    const gender = patient?.gender || c?.patient_gender;
    const patientPhone = patient?.phone || c?.patient_phone;
    const doctorName = c?.doctor_name || "—";
    const practice = c?.practice_location_name || c?.practice_name || c?.clinic_name || c?.practice;
    const initials = patientFullName.split(/\s+/).filter(Boolean).slice(0, 2).map((s) => s[0]).join("").toUpperCase() || "—";

    return (
        <section
            className="px-6 sm:px-10 py-6 border-b border-border/70 grid grid-cols-1 md:grid-cols-2 gap-6"
            data-testid="treatment-plan-identity-block"
        >
            {/* Patient card */}
            <div>
                <div className="text-[10px] font-bold tracking-[0.2em] uppercase text-muted-foreground mb-2">
                    Patient
                </div>
                <div className="flex items-start gap-3">
                    <div className="shrink-0 w-12 h-12 rounded-full bg-primary/15 text-primary font-display font-bold text-base grid place-items-center ring-1 ring-primary/30">
                        {initials}
                    </div>
                    <div className="min-w-0">
                        <div className="font-display text-lg font-bold text-foreground leading-tight" data-testid="treatment-plan-patient-name">
                            {patientFullName}
                        </div>
                        <div className="text-[12px] text-muted-foreground flex items-center gap-x-3 gap-y-1 flex-wrap mt-1">
                            {age != null && <span><strong className="text-foreground/80">Age</strong> · {age} yrs</span>}
                            {gender && <span><strong className="text-foreground/80">Sex</strong> · {humanize(gender)}</span>}
                            {patientPhone && <span className="tabular-nums">{patientPhone}</span>}
                        </div>
                        {patient?.id && (
                            <div className="text-[10.5px] text-muted-foreground/80 mt-1 tabular-nums">
                                Patient ID · {patient.id.slice(0, 8)}…
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Treating doctor card */}
            <div>
                <div className="text-[10px] font-bold tracking-[0.2em] uppercase text-muted-foreground mb-2">
                    Treating Doctor
                </div>
                <div className="flex items-start gap-3">
                    <div className="shrink-0 w-12 h-12 rounded-full bg-secondary text-foreground/80 font-display font-bold text-base grid place-items-center ring-1 ring-border">
                        <Stethoscope size={20} weight="duotone" />
                    </div>
                    <div className="min-w-0">
                        <div className="font-display text-lg font-bold text-foreground leading-tight" data-testid="treatment-plan-doctor-name">
                            {doctorName}
                        </div>
                        {practice && (
                            <div className="text-[12px] text-muted-foreground flex items-center gap-1.5 mt-1 truncate">
                                <Buildings size={11} weight="duotone" className="shrink-0" />
                                <span className="truncate">{practice}</span>
                            </div>
                        )}
                        {c?.code && (
                            <div className="text-[10.5px] text-muted-foreground/80 mt-1 tabular-nums inline-flex items-center gap-1">
                                <Hash size={10} weight="bold" /> {c.code}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </section>
    );
}


/* ─────────── Plan meta strip ─────────── */

function MetaStrip({ c, plan, sentAt }) {
    const cells = [
        {
            label: "Planning service",
            value: c?.planning_mode === "self" ? "Self-Plan" : "Invisique™ CoE",
            icon: Sparkle,
        },
        {
            label: "Planner",
            value: "CoE Team",
            icon: UserIcon,
        },
        {
            label: "3D software",
            value: plan?.planning_software ? humanize(plan.planning_software) : "—",
            icon: ArrowsClockwise,
        },
        {
            label: "Plan sent",
            value: fmtLongDate(sentAt),
            icon: CalendarBlank,
        },
    ];
    return (
        <section
            className="px-6 sm:px-10 py-5 bg-secondary/40 border-b border-border/70 grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-4"
            data-testid="treatment-plan-meta-strip"
        >
            {cells.map((cell, i) => {
                const Icon = cell.icon;
                return (
                    <div key={i} className="min-w-0">
                        <div className="text-[9.5px] font-bold tracking-[0.18em] uppercase text-muted-foreground mb-1 inline-flex items-center gap-1">
                            <Icon size={9} weight="fill" />
                            {cell.label}
                        </div>
                        <div className="text-[13px] font-semibold text-foreground truncate" title={cell.value}>
                            {cell.value}
                        </div>
                    </div>
                );
            })}
        </section>
    );
}


/* ─────────── Salutation + planner's headline message ─────────── */

function Salutation({ doctorName, patientName, plannerName, plannerNotes }) {
    const safeDoctor = doctorName
        ? (doctorName.toLowerCase().startsWith("dr") ? doctorName : `Dr. ${doctorName}`)
        : "Doctor";
    /* Avoid "Prepared by Invisique Planning Team of our planning team."
       when the planner_name already contains "team". */
    const plannerSentence = plannerName
        ? (plannerName.toLowerCase().includes("team")
            ? `Prepared by the ${plannerName}.`
            : `Prepared by ${plannerName} of our planning team.`)
        : "";
    return (
        <section data-testid="treatment-plan-salutation" className="space-y-4">
            <div className="text-[14px] text-foreground leading-relaxed">
                <p className="font-display font-semibold text-foreground">
                    Dear {safeDoctor},
                </p>
                <p className="mt-2 text-[13.5px] text-foreground/80 leading-relaxed">
                    The Invisique™ Center of Excellence has completed the treatment plan for{" "}
                    <strong className="text-foreground">{patientName || "your patient"}</strong>. The plan below summarises every clinical decision — staging, attachments, IPR, auxiliaries, retention — for your review.
                    {plannerSentence ? ` ${plannerSentence}` : ""}
                </p>
            </div>

            {plannerNotes && (
                <blockquote className="relative rounded-xl bg-primary/[0.05] border border-primary/20 px-5 py-4 sm:px-6 sm:py-5">
                    <Quotes size={28} weight="fill" className="absolute -top-2 left-3 text-primary/30" />
                    <div className="relative">
                        <div className="text-[10px] font-bold tracking-[0.2em] uppercase text-primary mb-1.5">
                            Note from your planning team
                        </div>
                        <div
                            className="text-[13.5px] text-foreground/90 leading-relaxed whitespace-pre-line font-display"
                            data-testid="treatment-plan-planner-notes"
                        >
                            {plannerNotes}
                        </div>
                    </div>
                </blockquote>
            )}
        </section>
    );
}



/* ─────────── Refinement advisory block (iter98.99cr) ───────────
   Surfaces the planner's `plan.refinement_expected` flag + reason as a
   formal clinical disclosure inside the treatment-plan document. Sits
   right after the salutation so it can't be missed. Amber tone matches
   the in-portal callout in PlanApprovalHero so the visual language is
   consistent across both surfaces. */

function RefinementAdvisoryBlock({ reason }) {
    return (
        <section
            className="rounded-2xl border border-amber-300 bg-gradient-to-br from-amber-50 via-amber-50/40 to-white p-5 sm:p-6"
            data-testid="treatment-plan-refinement-advisory"
        >
            <div className="flex items-start gap-4">
                <div className="w-11 h-11 rounded-xl bg-amber-100 text-amber-800 grid place-items-center shrink-0 ring-1 ring-amber-200">
                    <ArrowsClockwise size={22} weight="duotone" />
                </div>
                <div className="flex-1 min-w-0">
                    <div className="text-[10px] font-bold tracking-[0.2em] uppercase text-amber-800">
                        Planner advisory · Clinical disclosure
                    </div>
                    <div className="mt-1 font-display text-[17px] font-bold text-amber-950 leading-tight">
                        Refinement round may be needed
                    </div>
                    {reason && (
                        <div
                            className="mt-2 text-[14px] text-amber-900/90 leading-relaxed whitespace-pre-wrap italic border-l-2 border-amber-300 pl-3"
                            data-testid="treatment-plan-refinement-reason"
                        >
                            &ldquo;{reason}&rdquo;
                        </div>
                    )}
                    <div className="mt-2.5 text-[12px] text-amber-800/80 leading-relaxed">
                        Your planner has flagged this case as one that may benefit from a refinement round after the first set of aligners completes treatment.
                        The refinement scope and any associated charges will be governed by the treatment tier&apos;s included refinement allowance.
                    </div>
                </div>
            </div>
        </section>
    );
}



/* ─────────── External viewer block ─────────── */

function ExternalViewerBlock({ href, software }) {
    return (
        <section data-testid="treatment-plan-external-viewer">
            <div className="text-[10px] font-bold tracking-[0.2em] uppercase text-muted-foreground mb-2">
                Interactive 3D plan
            </div>
            <a
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                className="group block rounded-xl border border-primary/30 bg-gradient-to-br from-primary/[0.07] to-primary/[0.02] hover:from-primary/[0.12] hover:to-primary/[0.04] transition-all px-5 py-4"
            >
                <div className="flex items-center gap-4">
                    <div className="shrink-0 w-12 h-12 rounded-xl bg-primary/15 grid place-items-center ring-1 ring-primary/30 group-hover:bg-primary group-hover:ring-primary transition-colors">
                        <ArrowUpRight size={22} weight="bold" className="text-primary group-hover:text-white transition-colors" />
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="font-display text-[15px] font-bold text-foreground leading-tight">
                            Open the interactive 3D viewer
                        </div>
                        <p className="text-[12.5px] text-muted-foreground leading-snug mt-0.5">
                            Stage-by-stage tooth-movement playback {software ? `· ${humanize(software)}` : ""}
                        </p>
                    </div>
                    <span className="hidden sm:inline-flex shrink-0 items-center gap-1 text-[11px] font-bold uppercase tracking-[0.14em] text-primary group-hover:translate-x-0.5 transition-transform">
                        Open <ArrowUpRight size={11} weight="bold" />
                    </span>
                </div>
            </a>
        </section>
    );
}


/* ─────────── Attached deliverables ─────────── */

function DeliverablesBlock({ files }) {
    return (
        <section data-testid="treatment-plan-deliverables">
            <div className="text-[10px] font-bold tracking-[0.2em] uppercase text-muted-foreground mb-2">
                Attached deliverables
            </div>
            <div className="rounded-xl border border-border bg-secondary/40 divide-y divide-border">
                {files.map((f) => (
                    <a
                        key={f.id}
                        href={`${process.env.REACT_APP_BACKEND_URL}/api/files/${f.id}?auth=${localStorage.getItem("ca_token")}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-3 px-4 py-3 text-[13px] hover:bg-card transition-colors"
                        data-testid={`treatment-plan-file-${f.id}`}
                    >
                        <FileText size={16} weight="duotone" className="text-primary shrink-0" />
                        <span className="flex-1 truncate font-medium">{f.name}</span>
                        <ArrowUpRight size={12} weight="bold" className="text-muted-foreground shrink-0" />
                    </a>
                ))}
            </div>
        </section>
    );
}


/* ─────────── Revision history ─────────── */

function RevisionHistoryBlock({ iterations, timeline }) {
    return (
        <section data-testid="treatment-plan-revision-history">
            <div className="text-[10px] font-bold tracking-[0.2em] uppercase text-muted-foreground mb-2">
                Plan revision history
            </div>
            <ol className="relative ml-3 border-l-2 border-border/70 pl-4 space-y-3">
                {iterations.map((it) => {
                    const isApproved = it.reason === "approved";
                    const isRevision = it.reason === "revision_requested";
                    const dotCls = isApproved
                        ? "bg-emerald-500 ring-emerald-100"
                        : isRevision
                            ? "bg-rose-500 ring-rose-100"
                            : "bg-primary ring-primary/20";
                    const noteEvt = isRevision
                        ? (timeline.filter((e) => e.action === "revision_requested" || e.action === "case_revision_requested")[0])
                        : null;
                    return (
                        <li key={`${it.version}-${it.reason}`} className="relative">
                            <span className={`absolute -left-[1.45rem] top-1 w-3 h-3 rounded-full ring-4 ${dotCls}`} />
                            <div className="text-[12.5px] font-semibold text-foreground">
                                v{it.version} · {humanize(it.reason)}
                            </div>
                            <div className="text-[11.5px] text-muted-foreground">
                                {fmtDateTime(it.created_at)}
                                {it.snapshotted_by_name ? ` · ${it.snapshotted_by_name}` : ""}
                            </div>
                            {noteEvt?.note && (
                                <div className="text-[12px] text-foreground/80 mt-1 italic">
                                    “{noteEvt.note}”
                                </div>
                            )}
                        </li>
                    );
                })}
            </ol>
        </section>
    );
}


/* ─────────── Sign-off ─────────── */

function SignOff({ plannerName, statusKey, caseId }) {
    const isPending = statusKey === "sent_for_approval" || statusKey === "ready_for_review";
    const isApproved = statusKey === "approved";
    const isRevised = statusKey === "revision_requested";

    const isTeamName = plannerName && plannerName.toLowerCase().includes("team");
    const preparerLine1 = plannerName || "Invisique™ Planning Team";
    const preparerLine2 = isTeamName ? "Invisique™ Center of Excellence" : "Senior Treatment Planner";
    const preparerLine3 = isTeamName ? null : "Invisique™ Center of Excellence";

    return (
        <section
            className="pt-4 border-t border-border/70 grid grid-cols-1 md:grid-cols-2 gap-6"
            data-testid="treatment-plan-signoff"
        >
            <div>
                <div className="text-[10px] font-bold tracking-[0.2em] uppercase text-muted-foreground mb-2">
                    Prepared by
                </div>
                <div className="text-sm">
                    <div className="font-display text-base font-bold text-foreground">{preparerLine1}</div>
                    <div className="text-[12px] text-muted-foreground">{preparerLine2}</div>
                    {preparerLine3 && (
                        <div className="text-[12px] text-muted-foreground">{preparerLine3}</div>
                    )}
                </div>
                <div className="mt-4 pt-3 border-t border-dashed border-border/70 text-[11px] text-muted-foreground italic">
                    Digitally issued. No wet signature required — authenticity is established by your portal login + the audit timeline below.
                </div>
            </div>

            <div className="md:border-l md:border-border/70 md:pl-6">
                <div className="text-[10px] font-bold tracking-[0.2em] uppercase text-muted-foreground mb-2">
                    Your decision
                </div>
                {isPending && (
                    <div className="rounded-xl bg-amber-50 border border-amber-200 p-4 text-[12.5px] text-amber-900 leading-relaxed">
                        <strong className="font-semibold">Awaiting your approval.</strong>
                        <p className="mt-1">Return to the case page in your provider portal to either approve this plan or send the planning team a per-section revision request.</p>
                        <Link
                            to={`/doctor/cases/${caseId}`}
                            className="mt-3 inline-flex items-center gap-1.5 text-amber-900 font-semibold hover:underline text-[12px]"
                            data-testid="treatment-plan-go-decide"
                        >
                            Go to decision page <ArrowUpRight size={11} weight="bold" />
                        </Link>
                    </div>
                )}
                {isApproved && (
                    <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-4 text-[12.5px] text-emerald-900 inline-flex items-start gap-2">
                        <CheckCircle size={14} weight="fill" className="text-emerald-700 shrink-0 mt-0.5" />
                        <span><strong className="font-semibold">Plan approved</strong> — manufacturing is now in motion.</span>
                    </div>
                )}
                {isRevised && (
                    <div className="rounded-xl bg-rose-50 border border-rose-200 p-4 text-[12.5px] text-rose-900 inline-flex items-start gap-2">
                        <ArrowUUpLeft size={14} weight="fill" className="text-rose-700 shrink-0 mt-0.5" />
                        <span><strong className="font-semibold">Revision requested.</strong> The planning team is preparing a revised plan and will resend shortly.</span>
                    </div>
                )}
            </div>
        </section>
    );
}


/* ─────────── Document footer ─────────── */

function DocFooter({ generatedAt, caseCode, planVersion }) {
    return (
        <footer
            className="px-6 sm:px-10 py-6 bg-secondary/40 border-t border-border/70 flex items-center justify-between gap-4 flex-wrap"
            data-testid="treatment-plan-footer"
        >
            <div className="flex items-center gap-3">
                <InvisiqueLogo size="sm" />
                <span className="text-[11px] text-muted-foreground hidden sm:inline">
                    Invisible Precision · Uniquely Yours
                </span>
            </div>
            <div className="text-[10.5px] text-muted-foreground tabular-nums text-right">
                Document · {caseCode} · v{planVersion}<br />
                Generated {fmtDateTime(generatedAt)} IST
            </div>
        </footer>
    );
}
